import type HBButtonGroup from '@honeybook/hbui/dist/components/HBButtonGroup/HBButtonGroup';
import type HBSwitch from '@honeybook/hbui/dist/components/HBSwitch/HBSwitch';
import type HBVisibilitySwitch from '@honeybook/hbui/dist/components/HBSwitch/HBVisibilitySwitch';
import type { ComponentProps, ReactElement, ReactNode } from 'react';
import type { TFuncKey } from 'react-i18next';

export interface IOption {
    label: string;
    value: string;
}

export type TEditableAttrType =
    | 'colorPicker'
    | 'select'
    | 'formatting'
    | 'imagePicker'
    | 'toggle' // TODO: rename to switch
    | 'text'
    | 'radio'
    | 'range'
    | 'checkbox'
    | 'custom'
    | 'templateActions'
    | 'chooseTemplate'
    | 'visibilitySwitch';

export type TImportSidebarType = 'page' | 'block';

export interface IEditableAttr {
    type: TEditableAttrType;
    key: string;
    label?: ReactNode;
    label_key?: TFuncKey;
    inline?: boolean;
    isStyleAttr?: boolean;
    disabled?: boolean;
}

// TODO: question for Lorenzo regarding the font color prop
// zpl://screen?sid=5e1d7b78c28e967f7943b685&pid=5d83a80f331c222e828469a5
export interface IColorPickerAttr extends IEditableAttr {
    type: 'colorPicker';
    disableAlpha?: boolean;
    removeTransparent?: boolean;
}

export interface ISelectAttr extends IEditableAttr {
    type: 'select';
    options: IOption[];
    defaultValue?: string;
}

export interface IRadioAttr extends IEditableAttr {
    type: 'radio';
    options: ComponentProps<typeof HBButtonGroup>['buttons'];
    defaultValue?: string;
    additionalProps?: Partial<ComponentProps<typeof HBButtonGroup>>;
}

export interface IImagePickerAttr extends IEditableAttr {
    type: 'imagePicker';
}

// TODO: rename to ISwitchAttr
export interface IToggleAttr extends IEditableAttr {
    type: 'toggle';
    defaultValue: boolean;
    additionalProps?: Partial<ComponentProps<typeof HBSwitch>> & {
        label: string;
    };
}

export interface IVisibilitySwitchAttr extends IEditableAttr {
    type: 'visibilitySwitch';
    defaultValue: boolean;
    additionalProps?: Partial<ComponentProps<typeof HBVisibilitySwitch>> & {
        label: string;
    };
}

export interface ICheckboxAttr extends IEditableAttr {
    type: 'checkbox';
    name: string;
    defaultValue: boolean;
}

export interface IRangeAttr extends IEditableAttr {
    type: 'range';
    min: number;
    max: number;
    stepSize?: number;
    defaultValue: number;
}

type TTextAttrKey = 'placeholder' | 'label';

// For plain text properties such as: placeholder, label
export interface ITextAttr extends IEditableAttr {
    type: 'text';
    key: TTextAttrKey;
    // TODO: all property types that can be defined on a text attributes
    // I.E - font color, font size, font weight
}

export interface ICustomAttr extends IEditableAttr {
    type: 'custom';

    [x: string]: any;
}

export interface IBlockTemplateAttr extends IEditableAttr {
    type: 'templateActions';
}

export interface IChangeTemplateAttr extends IEditableAttr {
    type: 'chooseTemplate';
}

// Add any other prop type here!
export type IBlockAttr =
    | IColorPickerAttr
    | ISelectAttr
    | IImagePickerAttr
    | IToggleAttr
    | IRadioAttr
    | ICustomAttr
    | IRangeAttr
    | IBlockTemplateAttr
    | IChangeTemplateAttr
    | IVisibilitySwitchAttr;

export type IPageAttr = IColorPickerAttr | IRangeAttr;

export type IVariableAttr = IRadioAttr | IToggleAttr | ICheckboxAttr;

export enum ImageSidebarScreen {
    SelectedImage = 'selected_image',
    SelectImage = 'select_image'
}

export enum ContainerSidebarScreen {
    Main = 'main',
    CompanyInfo = 'company_info',
    SelectImage = 'select_image',
    SelectedBackgroundImage = 'selected_background_image',
    SelectCompositionLayout = 'select_composition_layout',
    SelectCompanyInfoLayout = 'select_company_info_layout',
    ChangeContainer = 'change_container',
    SelectQuestionComposition = 'select_question_composition',
    SelectTextComposition = 'select_text_composition',
    SelectImageComposition = 'select_image_composition',
    SelectVideoComposition = 'select_video_composition'
}

export enum LeadDetailsSidebarScreen {
    Main = 'main'
}

export enum TextSidebarScreen {
    Main = 'main'
}

export enum SocialSidebarScreen {
    Main = 'main'
}

export enum ContractSidebarScreen {
    Main = 'main',
    ContractContent = 'contract_content',
    ChangeContract = 'change_contract',
    SelectBlock = 'block_select',
    SmartField = 'smart_field',
    PaymentsRecap = 'payments_recap',
    ServicesRecap = 'services_recap'
}

export enum ServiceSelectionSidebarScreen {
    Main = 'main',
    SelectImage = 'select_image',
    SelectedImage = 'selected_image',
    SubItem = 'sub_item',
    ChangeService = 'change_service',
    SelectSession = 'select_session'
}

export enum InvoiceSidebarScreen {
    Main = 'main',
    ChangeInvoice = 'change_invoice',
    ItemOrPackage = 'service_item_block',
    SelectImage = 'select_image',
    SelectedImage = 'selected_image'
}

export enum ScheduleContainerSidebarScreen {
    Main = 'main',
    ChooseSession = 'choose_session'
}

export enum ChargeSidebarScreen {
    Main = 'main'
}

export enum QuestionSidebarScreen {
    Initial = 'initial',
    SelectedQuestion = 'selected_question',
    ChangeQuestionType = 'change_question_type',
    ChangeQuestionLinkedType = 'change_question_linked_type'
}

export enum NavigationSidebarScreen {
    Main = 'main'
}

export enum VideoSidebarScreen {
    Main = 'main'
}

export enum PageSettingsSidebarScreen {
    Main = 'main',
    SelectImage = 'select_image',
    SelectedBackgroundImage = 'selected_background_image'
}

export enum SpacerSidebarScreen {
    Main = 'main'
}

export enum SignatureSidebarScreen {
    Main = 'main'
}

export enum CompanyInfoSidebarScreen {
    Main = 'main',
    Link = 'link',
    SelectLayout = 'select_layout',
    ChangeCompanyInfo = 'change_company_info'
}

export enum ImportFlowComponentsScreen {
    flowComponentsForPage = 'flow_components_for_page',
    flowComponentsForBlock = 'flow_components_for_block',
    flowComponentsForImage = 'flow_components_for_image',
    flowComponentsForText = 'flow_components_for_text',
    flowComponentsForVideo = 'flow_components_for_video',
    flowComponentsForQuestion = 'flow_components_for_question'
}

export enum ImportFlowComponentsInnerScreen {
    FlowSelect = 'flow_select',
    TemplateSelect = 'page_select'
}

export type TSelectedType = {
    [templateId: string]: boolean;
};

export type NonBlockSidebarScreens = ImportFlowComponentsScreen;

export enum IframeSidebarScreen {
    Main = 'main'
}

export type SidebarScreen =
    | ChargeSidebarScreen
    | CompanyInfoSidebarScreen
    | ContainerSidebarScreen
    | ContractSidebarScreen
    | IframeSidebarScreen
    | ImageSidebarScreen
    | InvoiceSidebarScreen
    | LeadDetailsSidebarScreen
    | NavigationSidebarScreen
    | NonBlockSidebarScreens
    | PageSettingsSidebarScreen
    | QuestionSidebarScreen
    | ScheduleContainerSidebarScreen
    | ServiceSelectionSidebarScreen
    | SignatureSidebarScreen
    | SocialSidebarScreen
    | SpacerSidebarScreen
    | TextSidebarScreen
    | VideoSidebarScreen;

export interface ISidebarScreenConfig {
    headerProps: {
        title: string;
        withSeparator?: boolean;
        shouldHideBackArrow?: boolean;
        closeIcon?: ReactElement;
        showBackButton?: boolean;
        isHeaderWide?: boolean;
        onCloseCallback?: () => void;
    };
    component: Function;
    parent?: SidebarScreen;
    width?: number;
}

type TSidebarConfigInstances = {
    [key in SidebarScreen]?: ISidebarScreenConfig;
};

export interface ISidebarConfig extends TSidebarConfigInstances {
    initialScreen: SidebarScreen;
}

export const SSSCustomizeLearnMoreLink =
    'https://help.honeybook.com/en/articles/8891130-adding-services-with-scheduling-to-a-smart-file-template';
